/**
 * SWD destination A2 — Job Detail's destination, intended and actual side by side (Corbin,
 * 2026-10-17: "there is an intended disposal and an actual disposal"). Each side names the place
 * and its kind: a disposal facility, the company's drop-off location, or a catalog disposal well
 * it authorized (SWD or INJ alike). The actual side is "Not recorded yet" until the driver
 * arrives; when the load went somewhere other than planned, it says so.
 *
 * Billing prices the ACTUAL destination (#906). Recording a missing actual disposal from the office
 * is A1b's, on the In Review panel, not here.
 */

import * as React from 'react';
import type { JobDetail } from './use-job-detail';
import {
  DESTINATION_KIND_LABELS,
  actualPlace,
  intendedPlace,
  wentElsewhere,
  type DestinationPlace,
} from './destination-names';

const labelStyle: React.CSSProperties = {
  fontSize: '12px',
  fontWeight: 500,
  color: 'var(--color-text-secondary)',
};
const valueStyle: React.CSSProperties = {
  fontSize: '14px',
  color: 'var(--color-text-primary)',
  wordBreak: 'break-word',
};
const kindStyle: React.CSSProperties = { fontSize: '12px', color: 'var(--color-text-secondary)' };

function Side({
  title,
  place,
  empty,
  testId,
}: {
  title: string;
  place: DestinationPlace | null;
  empty: string;
  testId: string;
}): React.JSX.Element {
  return (
    <div
      data-testid={testId}
      style={{ display: 'flex', flexDirection: 'column', gap: '2px', minWidth: 0 }}
    >
      <span style={labelStyle}>{title}</span>
      {place === null ? (
        <span style={{ ...valueStyle, color: 'var(--color-text-secondary)' }}>{empty}</span>
      ) : (
        <>
          <span style={valueStyle}>{place.name}</span>
          <span style={kindStyle}>{DESTINATION_KIND_LABELS[place.kind]}</span>
        </>
      )}
    </div>
  );
}

export function DisposalDestinationsPanel({
  job,
}: {
  job: Pick<
    JobDetail,
    | 'plannedDisposalFacility'
    | 'actualDisposalFacility'
    | 'plannedDropoffHaulerLocation'
    | 'actualDropoffHaulerLocation'
    | 'plannedDropoffDisposalWell'
    | 'actualDropoffDisposalWell'
  >;
}): React.JSX.Element {
  const elsewhere = wentElsewhere(job);
  return (
    <section
      aria-label="Destination"
      className="sm:col-span-2"
      style={{ display: 'flex', flexDirection: 'column', gap: '6px', minWidth: 0 }}
    >
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
        <Side
          title="Intended destination"
          place={intendedPlace(job)}
          empty="Not assigned"
          testId="jdm-destination-intended"
        />
        <Side
          title="Actual destination"
          place={actualPlace(job)}
          empty="Not recorded yet"
          testId="jdm-destination-actual"
        />
      </div>
      {elsewhere && (
        <span role="note" style={{ ...kindStyle, color: 'var(--color-warning)' }}>
          The driver took this load somewhere other than planned. It is billed at the actual
          destination.
        </span>
      )}
    </section>
  );
}
